import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import BottomSheet from '../atoms/BottomSheet'
import { OtpInputView, PhoneInputView } from '../organisms/OtpFlow'
import {
  authenticateUserRequest,
  handleOtpCodeChange,
  handlePhoneNumberChange,
  handleResendTimerChange,
  handleTnCChange,
  resetSendOtp,
  sendOtpRequest,
  verifyOtpRequest,
} from '../../redux/slices'
import { analytics, trackEventCKD } from '../../analytics'
import { EVENTS } from '../../analytics/events'
import useLeadId from '../../hooks/useLead'
import TrackingPixel from '../atoms/TrackingPixel'

const LoginBottomSheet = forwardRef(
  (
    {
      buttonTitle = 'Continue',
      onLoginSuccess = () => {},
      sheetHeader = 'Enter mobile number',
      from,
    },
    ref,
  ) => {
    const phoneSheetRef = useRef(null)
    const timerRef = useRef(null)
    const dispatch = useDispatch()

    const {
      sendOtp,
      verifyOtp,
      userDetails,
      phoneNumber,
      tnc,
      otpCode,
      isAuthorized,
    } = useSelector((state) => state.auth)

    const leadId = useLeadId()

    const [resendTimer, setResendTimer] = useState(30)
    const [isTimerActive, setIsTimerActive] = useState(false)
    const [hasSentOTP, setHasSentOTP] = useState(
      localStorage.getItem('hasSentOTP') === 'true' || false,
    )
    // const [phoneNumber, setPhoneNumber] = useState('')
    // const [tnc, setTnc] = useState(true)
    // const [otpCode, setOtpCode] = useState('')

    useImperativeHandle(ref, () => ({
      show: () => phoneSheetRef?.current?.open(),
      hide: () => phoneSheetRef?.current?.hide(),
    }))

    const startTimer = () => {
      setIsTimerActive(true)
      setResendTimer(30)

      if (timerRef.current) {
        clearInterval(timerRef.current)
      }

      timerRef.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current)
            setIsTimerActive(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    const onPressSendOTP = () => {
      if (phoneNumber.length !== 10) return
      if (from) {
        if (from === 'foodscan') {
          console.log('trackEventCKD --> CKD_LKF_MobileNumberSubmit_StartPNDS')
          trackEventCKD(EVENTS.CKD_LKF_MobileNumberSubmit_StartPNDS)
        } else if (from === 'diagnostics') {
          console.log(
            'trackEventCKD --> CKD_LKF_MobileNumberSubmit_StartDIAGNOSTICS',
          )
          trackEventCKD(EVENTS.CKD_LKF_MobileNumberSubmit_StartDIAGNOSTICS, {
            contact_no: phoneNumber,
          })
        } else if (from === 'kidneyscore') {
          // trackEventCKD(EVENTS.CKD_LKF_MobileNumberSubmit_StartSCORE)
        } else if (from === 'result') {
          // trackEventCKD(EVENTS.CKD_LKF_MobileNumberSubmit_StartSCORE)
        }
      }
      dispatch(
        sendOtpRequest({
          contactNo: Number(phoneNumber),
          otpType: 'LOGIN',
          meta: {},
          patient_meta_info: {
            support_program_type: 'Life Ka Filter',
          },
        }),
      )
      startTimer()
    }

    const onPressResendOTP = () => {
      if (isTimerActive) return
      dispatch(
        sendOtpRequest({
          contactNo: Number(phoneNumber),
          otpType: 'LOGIN',
          meta: {},
          patient_meta_info: {
            support_program_type: 'Life Ka Filter',
          },
        }),
      )
      startTimer()
    }

    const onPressVerifyOTP = () => {
      if (otpCode?.length !== 6) return
      document.activeElement?.blur()
      dispatch(
        verifyOtpRequest({
          contactNo: Number(phoneNumber),
          otpType: 'LOGIN',
          otp: Number(otpCode),
          patient_meta_info: {
            support_program_type: 'Life Ka Filter',
          },
        }),
      )
      setHasSentOTP(true)
      localStorage.setItem('hasSentOTP', true)
    }

    const onPressEditContactNumber = () => {
      dispatch(handleOtpCodeChange(''))
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      setResendTimer(30)
      setIsTimerActive(false)
      dispatch(resetSendOtp())
    }

    const setMoengageUser = () => {
      const payload = {
        name: userDetails?.data?.name,
        country_code: userDetails?.data?.country_code,
        contact_no: userDetails?.data?.contact_no,
        email: userDetails?.data?.email,
        gender: userDetails?.data?.gender,
        dob: userDetails?.data?.dob,
      }
      analytics.setUserProperties(payload)
    }

    useEffect(() => {
      if (!isAuthorized && sendOtp.success) onPressVerifyOTP()
    }, [otpCode, sendOtp.success, isAuthorized])

    useEffect(() => {
      if (!isAuthorized && verifyOtp.success) {
        dispatch(
          authenticateUserRequest({
            contact_no: Number(phoneNumber),
            patient_meta_info: {
              support_program_type: 'Life Ka Filter',
            },
          }),
        )
      }
    }, [verifyOtp.success, isAuthorized])

    useEffect(() => {
      if (userDetails.success && isAuthorized) {
        phoneSheetRef?.current?.hide()
        setMoengageUser()
        onLoginSuccess(userDetails, hasSentOTP)
        setHasSentOTP(false)
        localStorage.setItem('hasSentOTP', false)
      }
    }, [userDetails.success, isAuthorized])

    useEffect(() => {
      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current)
        }
      }
    }, [])

    return (
      <BottomSheet ref={phoneSheetRef} onBackdropClick={() => {}}>
        {sendOtp.success ? (
          <>
            {/* {!isAuthorized && <TrackingPixel leadId={leadId} />} */}
            <OtpInputView
              contactNumber={phoneNumber}
              handleSendOtp={onPressVerifyOTP}
              onPressEditContactNumber={onPressEditContactNumber}
              otpCode={otpCode}
              resendTimer={resendTimer}
              setOtpCode={(code) => dispatch(handleOtpCodeChange(code))}
              verifyOtpErrorMessage={verifyOtp?.error || userDetails?.error}
              onPressResendOTP={onPressResendOTP}
              isTimerActive={isTimerActive}
            />
          </>
        ) : (
          <PhoneInputView
            phoneNumber={phoneNumber}
            setPhoneNumber={(num) => dispatch(handlePhoneNumberChange(num))}
            tnc={tnc}
            setTnc={(t) => handleTnCChange(t)}
            onPressSubmit={onPressSendOTP}
            buttonTitle={buttonTitle}
            sheetHeader={sheetHeader}
            loading={sendOtp.loading}
          />
        )}
      </BottomSheet>
    )
  },
)

export default memo(LoginBottomSheet)
